// Calcul du stock de sécurité, du point de commande et de la quantité à commander.
// La feuille copiée par « Copier pour Excel » (toExcel) et le modèle .xlsx (modeleStock.js) refont
// exactement les mêmes calculs : toute modification d'une formule ici doit y être reportée.
//
// Conventions (choisies avec un regard métier, voir le commit du 2026-10-08) :
// - la demande moyenne compte tous les canaux, mais la variabilité ne compte que Amazon et le site :
//   les commandes B2B arrivent par gros volumes connus d'avance, pas au hasard ;
// - la variabilité du délai se déduit du délai le plus court et du plus long constatés : (max − min) / 3 ;
// - la hausse saisonnière ne joue que sur la période couverte par la commande, pas sur les ventes
//   d'aujourd'hui (sinon fausse alerte de rupture) ;
// - la quantité est arrondie au minimum de commande et au colisage ;
// - une vente manquée pendant une rupture est perdue, pas reportée.

// z = LOI.NORMALE.STANDARD.INVERSE.N(niveau de service), à 7 chiffres comme Excel.
export const SERVICE_LEVELS = [
  { value: '0.9', z: 1.2815516, label: '90 %' },
  { value: '0.95', z: 1.6448536, label: '95 %' },
  { value: '0.97', z: 1.8807936, label: '97 %' },
  { value: '0.98', z: 2.0537489, label: '98 %' },
  { value: '0.99', z: 2.3263479, label: '99 %' }
];

// Exemple fictif : une gourde vendue sur Amazon et sur Shopify, fabriquée en Asie.
// Chaque semaine : [Amazon, Shopify, B2B]. L'exemple n'a pas de ventes B2B.
// Délai le plus court 65 j, le plus long 86 j : écart-type du délai (86 − 65) / 3 = 7 jours.
export const EXAMPLE = {
  weeks: [
    ['72', '24', ''], ['65', '31', ''], ['81', '22', ''], ['77', '28', ''], ['69', '35', ''], ['88', '26', ''],
    ['74', '30', ''], ['70', '27', ''], ['93', '33', ''], ['79', '29', ''], ['84', '25', ''], ['76', '32', '']
  ],
  production: '30',
  transport: '35',
  reception: '10',
  leadMin: '65',
  leadMax: '86',
  service: '0.95',
  season: '0',
  stock: '1450',
  transit: '0',
  transitIn: '20',
  cycle: '30',
  moq: '',
  pack: '',
  cost: '7.20'
};

export const MAX_WEEKS = 52;
export const CONSEIL_SEMAINES = 13;

export const num = (v) => {
  const n = parseFloat(String(v).replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

// Une valeur négative n'a pas de sens pour un délai, un stock ou des ventes : elle compte pour 0.
// La saison peut baisser les ventes, mais pas de plus de 100 %. Mêmes règles dans toExcel.
export const pos = (v) => Math.max(0, num(v) || 0);
export const seasonPct = (v) => Math.max(-100, num(v) || 0);
export const packOf = (v) => Math.max(1, pos(v));

const mean = (xs) => xs.reduce((a, b) => a + b, 0) / xs.length;
const sampleSd = (xs) => {
  const m = mean(xs);
  return Math.sqrt(xs.reduce((a, x) => a + (x - m) ** 2, 0) / (xs.length - 1));
};

export const CHANNELS = ['Amazon', 'Shopify', 'B2B'];

// Semaines retenues : celles où au moins un canal est renseigné (une case vide compte pour 0).
export const filledWeeks = (weeks) => weeks
  .map((w) => CHANNELS.map((_, j) => num(w[j])))
  .filter((w) => w.some((x) => x !== null))
  .map((w) => w.map((x) => Math.max(0, x || 0)));

// Écart-type du délai à partir du plus court et du plus long constatés (≈ 10 commandes).
export const leadSdOf = (lo, hi) => Math.max(0, pos(hi) - pos(lo)) / 3;

// Minimum de commande puis colisage, comme dans le tableur.
export const roundOrder = (q, moq, pack) => (q > 0 ? Math.ceil(Math.max(q, moq) / pack) * pack : 0);

const safetyFor = (z, lead, sd, d, leadSd) => Math.ceil(z * Math.sqrt(lead * sd ** 2 + d ** 2 * leadSd ** 2));

export function compute(p) {
  const rows = filledWeeks(p.weeks);
  if (rows.length < 2) return { error: 'Renseignez au moins deux semaines de ventes.' };
  const totals = rows.map((w) => w[0] + w[1] + w[2]);
  const b2c = rows.map((w) => w[0] + w[1]);
  const d = mean(totals) / 7;
  if (!(d > 0)) return { error: 'Les ventes sont nulles : rien à calculer.' };
  const sdDay = sampleSd(b2c) / Math.sqrt(7);
  const lead = pos(p.production) + pos(p.transport) + pos(p.reception);
  const leadSd = leadSdOf(p.leadMin, p.leadMax);
  const idx = Math.max(0, SERVICE_LEVELS.findIndex((l) => l.value === p.service));
  const level = SERVICE_LEVELS[idx] || SERVICE_LEVELS[1];
  const demandPart = lead * sdDay ** 2;
  const leadPart = d ** 2 * leadSd ** 2;
  const safety = safetyFor(level.z, lead, sdDay, d, leadSd);
  const reorder = Math.ceil(d * lead + safety);
  const stock = pos(p.stock);
  const transit = pos(p.transit);
  const position = stock + transit;
  const cycle = pos(p.cycle);
  const k = 1 + seasonPct(p.season) / 100;
  const dSeason = d * k;
  const moq = pos(p.moq);
  const pack = packOf(p.pack);
  // Niveau à atteindre : la demande pendant le délai, le stock de sécurité, puis la période couverte.
  const qtyRaw = Math.max(0, Math.ceil(d * lead + safety + dSeason * cycle - Math.min(position, reorder)));
  const qty = roundOrder(qtyRaw, moq, pack);
  const cost = pos(p.cost);
  const next = SERVICE_LEVELS[idx + 1];
  const nextSafety = next ? safetyFor(next.z, lead, sdDay, d, leadSd) : null;
  const sum = totals.reduce((a, b) => a + b, 0);
  const share = (j) => rows.reduce((a, w) => a + w[j], 0) / sum;
  return {
    weeks: rows.length,
    d,
    dSeason,
    sdDay,
    lead,
    leadSd,
    z: level.z,
    service: Number(level.value),
    serviceLabel: level.label,
    safety,
    reorder,
    stock,
    transit,
    transitIn: Math.max(0, Math.round(num(p.transitIn) || 0)),
    position,
    cycle,
    moq,
    pack,
    cover: stock / d,
    daysToOrder: Math.max(0, Math.ceil((position - reorder) / d)),
    qtyRaw,
    qty,
    orderCover: dSeason > 0 ? qty / dSeason : 0,
    moqMonths: moq > 2 * dSeason * Math.max(cycle, 1) && dSeason > 0 ? moq / (dSeason * 30) : null,
    // Ce que pèse chaque incertitude, en unités de stock de sécurité.
    safetyReliableLead: safetyFor(level.z, lead, sdDay, d, 0),
    safetySteadySales: safetyFor(level.z, lead, 0, d, leadSd),
    leadShare: demandPart + leadPart > 0 ? leadPart / (demandPart + leadPart) : 0,
    // Environ une rupture tous les N mois, avec une commande tous les « cycle » jours.
    ruptureMonths: cycle > 0 ? cycle / (1 - level.value) / 30 : null,
    next: next ? { label: next.label, extra: nextSafety - safety } : null,
    cost,
    safetyValue: safety * cost,
    orderValue: qty * cost,
    amazonShare: share(0),
    shopifyShare: share(1),
    b2bShare: share(2)
  };
}

// Projection jour par jour du stock physique, à ventes moyennes : on commande dès que le stock
// (disponible + déjà commandé) passe sous le point de commande, et la commande arrive après le délai.
// La hausse saisonnière ne vaut que pour la période couverte par la prochaine commande : de sa réception
// à la fin de cette période ; les commandes suivantes reviennent au rythme normal. Une vente manquée est perdue.
export function project(r) {
  // Horizon : jusqu'à la fin de la période couverte par la prochaine commande (c'est elle qu'on décide).
  const seasonFrom = r.daysToOrder + Math.max(1, Math.round(r.lead));
  const seasonTo = seasonFrom + Math.round(r.cycle);
  const horizon = Math.min(240, Math.max(90, Math.ceil(seasonFrom + Math.max(r.cycle, r.orderCover) + 5)));
  let onHand = r.stock;
  let pipeline = r.transit > 0 ? [{ at: r.transitIn, qty: r.transit }] : [];
  const points = [];
  const orders = [];
  const arrivals = [];
  let stockout = null;
  let stockoutDays = 0;
  let lost = 0;
  for (let t = 0; t <= horizon; t++) {
    pipeline = pipeline.filter((o) => {
      if (o.at !== t) return true;
      onHand += o.qty;
      arrivals.push({ t, qty: o.qty });
      return false;
    });
    const rate = t >= seasonFrom && t < seasonTo ? r.dSeason : r.d;
    const position = onHand + pipeline.reduce((a, o) => a + o.qty, 0);
    if (position <= r.reorder) {
      const cover = orders.length === 0 ? r.dSeason : r.d;
      const qty = roundOrder(Math.ceil(r.d * r.lead + r.safety + cover * r.cycle - position), r.moq, r.pack);
      if (qty > 0) {
        pipeline.push({ at: t + Math.max(1, Math.round(r.lead)), qty });
        orders.push({ t, qty });
      }
    }
    if (onHand < rate) {
      if (stockout === null) stockout = t;
      stockoutDays += 1;
      lost += rate - onHand;
    }
    points.push(onHand);
    onHand = Math.max(0, onHand - rate);
  }
  return { horizon, points, orders, arrivals, stockout, stockoutDays, lost };
}

// Nombres pour Excel en français : virgule décimale (« 3.5 » serait lu comme du texte).
const fr = (v) => (typeof v === 'number' ? String(Math.round(v * 1e6) / 1e6).replace('.', ',') : v);

// Feuille Excel (version française) : coller en A1, les formules recalculent tout.
// A Semaine · B Amazon · C Shopify · D B2B · E Total · F Amazon + Shopify · (G vide) · H Paramètre · I Valeur.
// Mêmes lignes et mêmes formules que l'onglet « Votre produit » du modèle .xlsx (modeleStock.js).
export function toExcel(p) {
  const rows = filledWeeks(p.weeks);
  const n = rows.length;
  const last = n + 1;
  const params = [
    ['Paramètre', 'Valeur'],
    ['Fabrication fournisseur (jours)', pos(p.production)],
    ['Transport (jours)', pos(p.transport)],
    ['Réception entrepôt ou Amazon (jours)', pos(p.reception)],
    ['Délai total (jours)', '=SOMME(I2:I4)'],
    ['Délai total le plus court constaté (jours)', pos(p.leadMin)],
    ['Délai total le plus long constaté (jours)', pos(p.leadMax)],
    ['Niveau de service', Number((SERVICE_LEVELS.find((l) => l.value === p.service) || SERVICE_LEVELS[1]).value)],
    ['Hausse attendue sur la période couverte (%)', seasonPct(p.season)],
    ['Stock possédé (entrepôt, Amazon, envois vers Amazon)', pos(p.stock)],
    ['Commandé au fournisseur, pas encore reçu', pos(p.transit)],
    ['Période couverte par chaque commande (jours)', pos(p.cycle)],
    ['Minimum de commande (unités)', pos(p.moq)],
    ['Colisage (unités par carton)', packOf(p.pack)],
    ['Coût d\'achat rendu (€ HT par unité)', pos(p.cost)],
    ['', ''],
    ['Ventes par jour', `=MOYENNE(E2:E${last})/7`],
    ['Écart-type des ventes par jour (hors B2B)', `=ECARTYPE.STANDARD(F2:F${last})/RACINE(7)`],
    ['Écart-type du délai (jours)', '=MAX(0;I7-I6)/3'],
    ['Stock de sécurité', '=ARRONDI.SUP(LOI.NORMALE.STANDARD.INVERSE.N(I8)*RACINE(I5*I18^2+I17^2*I19^2);0)'],
    ['Point de commande', '=ARRONDI.SUP(I17*I5+I20;0)'],
    ['Couverture du stock en main (jours)', '=ARRONDI(I10/I17;0)'],
    ['Jours avant de commander', '=MAX(0;ARRONDI.SUP((I10+I11-I21)/I17;0))'],
    ['Quantité avant minimum et colisage', '=MAX(0;ARRONDI.SUP(I17*I5+I20+I17*(1+I9/100)*I12-MIN(I10+I11;I21);0))'],
    ['Quantité à commander', '=SI(I24<=0;0;ARRONDI.SUP(MAX(I24;I13)/MAX(1;I14);0)*MAX(1;I14))'],
    ['Valeur du stock de sécurité (€ HT)', '=I20*I15']
  ];
  const lines = [];
  const height = Math.max(n + 1, params.length);
  for (let i = 0; i < height; i++) {
    const left = i === 0 ? ['Semaine', ...CHANNELS, 'Total', 'Amazon + Shopify']
      : i <= n ? [`S${i}`, ...rows[i - 1].map(fr), `=B${i + 1}+C${i + 1}+D${i + 1}`, `=B${i + 1}+C${i + 1}`]
        : ['', '', '', '', '', ''];
    const right = (params[i] || ['', '']).map(fr);
    lines.push([...left, '', ...right].join('\t'));
  }
  return lines.join('\n');
}
